import { useState, useEffect, useCallback } from 'react'
import { Loader2 } from 'lucide-react'
import { templateTrainingApi } from '@/services/api/templateTraining'
import { timetableImportV2Api } from '@/services/api/timetableImportV2'
import { TokenMatchingModal } from './TokenMatchingModal'
import type { TokenMatch, UnmatchedToken, TokenOrigin, TokenPositions } from './typesV2'

function asTokenOrigin(value: unknown): TokenOrigin | null {
  if (!value || typeof value !== 'object') return null
  if (typeof (value as { source?: unknown }).source !== 'string') return null
  return value as TokenOrigin
}

interface TemplateEditModalProps {
  templateId: string
  templateName: string
  isOpen: boolean
  onClose: () => void
  onSaved: () => void
}

export function TemplateEditModal({ templateId, isOpen, onClose, onSaved }: TemplateEditModalProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [matchedTokens, setMatchedTokens] = useState<TokenMatch[]>([])
  const [unmatchedTokens, setUnmatchedTokens] = useState<UnmatchedToken[]>([])
  const [tokenPositions, setTokenPositions] = useState<Record<string, TokenPositions>>({})
  const [orphanPatterns, setOrphanPatterns] = useState<Array<{ regex: string; field: string; match_mode: string }>>([])
  const [splitTracking, setSplitTracking] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!isOpen || !templateId) return
    setLoading(true)
    setError('')
    templateTrainingApi.getTemplateConfig(templateId)
      .then(data => {
        const config = data.extraction_config as Record<string, unknown> | undefined
        if (!config) {
          setError('Keine Extraktionsdaten im Template gespeichert.')
          return
        }

        const autoAnswers = (config.auto_answers as Record<string, unknown>) || {}
        const allTokens = (config.all_tokens as string[]) || []
        const positions = (config.token_positions as Record<string, TokenPositions>) || {}
        const split = (config.split_tracking as Record<string, string>) || {}
        const orphans = (config.orphan_patterns as Array<{ regex: string; field: string; match_mode: string }>) || []
        const tokenPatterns = (config.token_patterns as Record<string, Array<{ token: string; confirmed?: boolean } | string>>) || {}
        
        // CRITICAL: Load token_history to get confirmed tokens
        const importLogSummary = (config.import_log_summary as Record<string, unknown>) || {}
        const tokenHistory = (importLogSummary.token_history as Record<string, Record<string, unknown>>) || {}

        setTokenPositions(positions)
        setOrphanPatterns(orphans)
        setSplitTracking(split)

        const matched: TokenMatch[] = []
        const resolvedTokens = new Set<string>()

        // Get _splits registry for checking split tokens
        const splitsRegistry = (tokenPatterns as Record<string, unknown>)._splits as Record<string, unknown> || {}

        // Load from auto_answers first
        for (const [token, info] of Object.entries(autoAnswers)) {
          if (typeof info === 'object' && info !== null) {
            const fieldInfo = info as Record<string, unknown>
            const field = (fieldInfo.field as string) || 'ignore'
            const confidence = (fieldInfo.confidence as number) || 0.9
            const pos = positions[token] || { cols: [], rows: [], count: 0 }
            
            // Check if token is in _splits registry (has been split)
            const splitInfo = splitsRegistry[token.toLowerCase()] as Record<string, unknown> | undefined
            if (splitInfo) {
              const splitParts = splitInfo.parts as string[]
              matched.push({
                token,
                field: 'ignore',
                confidence: 0.99,
                positions: pos,
                pattern_source: 'split_original',
                auto_confirmed: true,
                wasPreviouslyConfirmed: true,
                is_split: true,
                auto_split: splitInfo.auto_split !== false,
                split_into: splitParts,
                origin: asTokenOrigin(fieldInfo.origin) ?? asTokenOrigin(splitInfo.origin),
              })
              resolvedTokens.add(token.toLowerCase())
              continue
            }
            
            // Case-insensitive lookup in token_history
            const historyKey = Object.keys(tokenHistory).find(k => k.toLowerCase() === token.toLowerCase())
            const historyEntry = historyKey ? tokenHistory[historyKey] : null
            const isConfirmed = historyEntry?.confirmed === true
            
            matched.push({ 
              token, field, confidence, positions: pos, 
              pattern_source: (fieldInfo.source as string) || null, 
              auto_confirmed: isConfirmed, 
              wasPreviouslyConfirmed: isConfirmed,
              auto_split: fieldInfo.auto_split === true || historyEntry?.auto_split === true,
              origin: asTokenOrigin(fieldInfo.origin) ?? asTokenOrigin(historyEntry?.origin),
            })
            resolvedTokens.add(token.toLowerCase())
          }
        }

        // Also load from token_patterns if not already in auto_answers
        // This ensures tokens saved via TemplateEditModal are shown
        
        for (const [field, tokens] of Object.entries(tokenPatterns)) {
          if (!Array.isArray(tokens) || field === '_splits') continue
          for (const entry of tokens) {
            const tokenStr = typeof entry === 'string' ? entry : entry.token
            if (!tokenStr || resolvedTokens.has(tokenStr.toLowerCase())) continue // Case-insensitive check
            
            // Check if token is in _splits registry (has been split)
            const splitInfo = splitsRegistry[tokenStr.toLowerCase()] as Record<string, unknown> | undefined
            if (splitInfo) {
              // Token has been split - show with special status
              const splitParts = splitInfo.parts as string[]
              const pos = positions[tokenStr] || { cols: [], rows: [], count: 0 }
              
              const entryOrigin = typeof entry === 'object' && entry !== null
                ? asTokenOrigin((entry as { origin?: unknown }).origin)
                : null
              matched.push({
                token: tokenStr,
                field: 'ignore', // Split tokens should be ignored
                confidence: 0.99,
                positions: pos,
                pattern_source: 'split_original',
                auto_confirmed: true,
                wasPreviouslyConfirmed: true,
                is_split: true, // Mark as split
                auto_split: splitInfo.auto_split !== false || (typeof entry === 'object' && entry !== null && (entry as { auto_split?: boolean }).auto_split === true),
                split_into: splitParts, // Show split parts
                origin: entryOrigin ?? asTokenOrigin(splitInfo.origin),
              })
              
              resolvedTokens.add(tokenStr.toLowerCase())
              continue
            }
            
            // Regular token loading
            const pos = positions[tokenStr] || { cols: [], rows: [], count: 0 }
            
            // Case-insensitive lookup in token_history
            const historyKey = Object.keys(tokenHistory).find(k => k.toLowerCase() === tokenStr.toLowerCase())
            const historyEntry = historyKey ? tokenHistory[historyKey] : null
            const isConfirmed = historyEntry?.confirmed === true
            
            const entryOrigin = typeof entry === 'object' && entry !== null
              ? asTokenOrigin((entry as { origin?: unknown }).origin)
              : null
            matched.push({ 
              token: tokenStr, 
              field, 
              confidence: isConfirmed ? 0.95 : 0.9, 
              positions: pos, 
              pattern_source: 'template_pattern', 
              auto_confirmed: isConfirmed, 
              wasPreviouslyConfirmed: isConfirmed,
              auto_split: (typeof entry === 'object' && entry !== null && (entry as { auto_split?: boolean }).auto_split === true) || historyEntry?.auto_split === true,
              origin: entryOrigin ?? asTokenOrigin(historyEntry?.origin),
            })
            resolvedTokens.add(tokenStr.toLowerCase())
          }
        }

        // CRITICAL: Also load confirmed tokens from token_history that aren't in auto_answers or token_patterns
        // This ensures that tokens confirmed in previous sessions are preserved
        for (const [token, historyEntry] of Object.entries(tokenHistory)) {
          if (!token || resolvedTokens.has(token.toLowerCase())) continue // Case-insensitive check
          
          // Check if token is in _splits registry (has been split)
          const splitInfo = splitsRegistry[token.toLowerCase()] as Record<string, unknown> | undefined
          if (splitInfo) {
            const splitParts = splitInfo.parts as string[]
            const pos = positions[token] || { cols: [], rows: [], count: 0 }
            matched.push({
              token,
              field: 'ignore',
              confidence: 0.99,
              positions: pos,
              pattern_source: 'split_original',
              auto_confirmed: true,
              wasPreviouslyConfirmed: true,
              is_split: true,
              auto_split: splitInfo.auto_split !== false,
              split_into: splitParts,
              origin: asTokenOrigin(historyEntry.origin) ?? asTokenOrigin(splitInfo.origin),
            })
            resolvedTokens.add(token.toLowerCase())
            continue
          }
          
          const isConfirmed = historyEntry.confirmed === true
          const field = (historyEntry.field as string) || 'ignore'
          if (isConfirmed) {
            const pos = positions[token] || { cols: [], rows: [], count: 0 }
            matched.push({ 
              token, 
              field, 
              confidence: 0.95, 
              positions: pos, 
              pattern_source: 'token_history', 
              auto_confirmed: true, 
              wasPreviouslyConfirmed: true,
              origin: asTokenOrigin(historyEntry.origin),
            })
            resolvedTokens.add(token.toLowerCase()) // Use lowercase
          }
        }

        const unmatched: UnmatchedToken[] = []
        for (const token of allTokens) {
          if (resolvedTokens.has(token) || token in autoAnswers) continue
          
          // Check if token is in _splits registry
          const splitInfo = splitsRegistry[token.toLowerCase()] as Record<string, unknown> | undefined
          if (splitInfo) {
            const splitParts = splitInfo.parts as string[]
            const pos = positions[token] || { cols: [], rows: [], count: 0 }
            matched.push({
              token,
              field: 'ignore',
              confidence: 0.99,
              positions: pos,
              pattern_source: 'split_original',
              auto_confirmed: true,
              wasPreviouslyConfirmed: true,
              is_split: true,
              auto_split: splitInfo.auto_split !== false,
              split_into: splitParts,
              origin: asTokenOrigin(splitInfo.origin),
            })
            resolvedTokens.add(token.toLowerCase())
            continue
          }
          
          const pos = positions[token] || { cols: [], rows: [], count: 0 }
          const suggestions = buildSuggestions(token, orphans)
          const splitFrom = split[token] || null
          const historyKey = Object.keys(tokenHistory).find(k => k.toLowerCase() === token.toLowerCase())
          unmatched.push({
            token,
            positions: pos,
            suggestions,
            split_from: splitFrom,
            origin: asTokenOrigin(historyKey ? tokenHistory[historyKey]?.origin : null),
          })
        }

        setMatchedTokens(matched)
        setUnmatchedTokens(unmatched)
      })
      .catch(() => setError('Laden fehlgeschlagen'))
      .finally(() => setLoading(false))
  }, [isOpen, templateId])

  const buildSuggestions = (token: string, orphans: Array<{ regex: string; field: string; match_mode: string }>) => {
    const suggestions: Array<{ field: string; confidence: number; reason: string }> = []
    for (const p of orphans) {
      try {
        const re = new RegExp(p.regex, 'i')
        if (p.match_mode === 'full' && re.test(token)) {
          suggestions.push({ field: p.field, confidence: 0.9, reason: 'orphan_pattern' })
        } else if (p.match_mode === 'prefix' && re.test(token)) {
          suggestions.push({ field: p.field, confidence: 0.7, reason: 'orphan_pattern_prefix' })
        }
      } catch {
        // ignore invalid regex
      }
    }
    if (/^\d{1,2}:\d{2}[-–]?\d{1,2}:\d{2}$/.test(token)) {
      suggestions.push({ field: 'time_slot', confidence: 0.8, reason: 'time_pattern' })
    } else if (/^[A-Z]{2,4}$/.test(token)) {
      suggestions.push({ field: 'teacher_code', confidence: 0.5, reason: 'code_pattern' })
    }
    return suggestions
  }

  const handleResolve = useCallback(async (resolutions: Array<{ 
    token: string
    field: string
    confirmed: boolean
    split?: boolean
    split_into?: string[]
    auto_split?: boolean
  }>) => {
    try {
      const data = await templateTrainingApi.getTemplateConfig(templateId)
      const config = data.extraction_config as Record<string, unknown> | undefined
      if (!config) return

      // ── 1. Update token_history (use original case) ──
      const summary = (config.import_log_summary as Record<string, unknown>) || {}
      const tokenHistory = (summary.token_history as Record<string, Record<string, unknown>>) || {}
      
      for (const r of resolutions) {
        if (!r.confirmed) continue
        if (r.split) continue  // Skip split tokens - they're handled separately
        
        // Use original case for storage
        const tokenKey = r.token
        
        // Case-insensitive lookup
        const existingKey = Object.keys(tokenHistory).find(k => k.toLowerCase() === tokenKey.toLowerCase())
        const existing = existingKey ? tokenHistory[existingKey] : null
        
        if (existing) {
          existing.field = r.field
          existing.confirmed = true
          existing.last_action = 'confirmed'
          existing.source = 'template_pattern'
          // DO NOT add split info here - split tokens go to _splits only
        } else {
          tokenHistory[tokenKey] = {
            field: r.field,
            confirmed: true,
            last_action: 'confirmed',
            source: 'template_pattern',
            // DO NOT add split info here
          }
        }
      }
      
      summary.token_history = tokenHistory
      config.import_log_summary = summary

      // ── 2. Update token_patterns (use original case) ──
      const tokenPatterns = (config.token_patterns as Record<string, Array<Record<string, unknown> | string>>) || {}
      const autoAnswers = (config.auto_answers as Record<string, Record<string, unknown>>) || {}
      
      for (const r of resolutions) {
        if (!r.confirmed) continue
        
        const token = r.token
        const tokenLower = token.toLowerCase()
        const newField = r.field
        
        // Handle split tokens specially - only go to _splits
        if (r.split && r.split_into) {
          // Step 1: Remove token from all token_patterns fields (case-insensitive)
          for (const [field, tokens] of Object.entries(tokenPatterns)) {
            if (!Array.isArray(tokens) || field === '_splits') continue
            const filtered = tokens.filter(entry => {
              const entryToken = typeof entry === 'string' ? entry : (entry.token as string)
              return entryToken.toLowerCase() !== tokenLower
            })
            if (filtered.length === 0) {
              delete tokenPatterns[field]
            } else {
              tokenPatterns[field] = filtered
            }
          }
          
          // Step 2: Remove from auto_answers
          const autoAnswersKey = Object.keys(autoAnswers).find(k => k.toLowerCase() === tokenLower)
          if (autoAnswersKey) {
            delete autoAnswers[autoAnswersKey]
          }
          
          // Step 3: Remove from token_history
          const tokenHistoryKey = Object.keys(tokenHistory).find(k => k.toLowerCase() === tokenLower)
          if (tokenHistoryKey) {
            delete tokenHistory[tokenHistoryKey]
          }
          
          // Step 4: Add to _splits registry
          const splitsRegistry = ((tokenPatterns as Record<string, unknown>)._splits as Record<string, unknown>) || {}
          splitsRegistry[tokenLower] = {
            original: token,
            parts: r.split_into,
            split_at: new Date().toISOString(),
            auto_split: Boolean(r.auto_split),
            source: r.auto_split ? 'auto_split' : 'manual_split',
          }
          ;(tokenPatterns as Record<string, unknown>)._splits = splitsRegistry
          
          continue // Don't add to regular field
        }
        
        // Step 2a: Remove token from all other fields (case-insensitive)
        for (const [field, tokens] of Object.entries(tokenPatterns)) {
          if (!Array.isArray(tokens) || field === '_splits') continue
          
          // Type check (Bug 12)
          if (!Array.isArray(tokens)) {
            console.warn(`[TemplateEditModal] tokenPatterns[${field}] is not an array, skipping`)
            continue
          }
          
          const filtered = tokens.filter(entry => {
            const entryToken = typeof entry === 'string' ? entry : (entry.token as string)
            return entryToken.toLowerCase() !== tokenLower
          })
          
          // Clean up empty arrays (Bug 9)
          if (filtered.length === 0) {
            delete tokenPatterns[field]
          } else {
            tokenPatterns[field] = filtered
          }
        }
        
        // Step 2b: Add token to new field with confirmed status
        if (!tokenPatterns[newField]) {
          tokenPatterns[newField] = []
        }
        
        // Check if token already exists in new field (case-insensitive)
        const existingIdx = tokenPatterns[newField].findIndex(entry => {
          const entryToken = typeof entry === 'string' ? entry : (entry.token as string)
          return entryToken.toLowerCase() === tokenLower
        })
        
        if (existingIdx >= 0) {
          // Update existing entry
          const existing = tokenPatterns[newField][existingIdx]
          if (typeof existing === 'object') {
            existing.confirmed = true
            existing.confirmed_at = new Date().toISOString()
            existing.source = 'template_pattern'
            // Remove requires_canonical_resolution if reassigning away from subject
            if (newField !== 'subject') {
              delete existing.requires_canonical_resolution
            }
            // Preserve origin (Bug 7)
          } else {
            // Replace string entry with object
            tokenPatterns[newField][existingIdx] = {
              token: token, // Use original case
              confirmed: true,
              confirmed_at: new Date().toISOString(),
              source: 'template_pattern',
            }
          }
        } else {
          // Add new entry - preserve origin from existing sources (Bug 7)
          const newEntry: Record<string, unknown> = {
            token: token, // Use original case
            confirmed: true,
            confirmed_at: new Date().toISOString(),
            source: 'template_pattern',
          }
          
          // Try to preserve origin from token_history
          const historyKey = Object.keys(tokenHistory).find(k => k.toLowerCase() === tokenLower)
          if (historyKey && tokenHistory[historyKey]?.origin) {
            newEntry.origin = tokenHistory[historyKey].origin
          }
          
          // Try to preserve origin from existing token_patterns entry
          for (const [field, tokens] of Object.entries(tokenPatterns)) {
            if (!Array.isArray(tokens) || field === '_splits') continue
            const existingEntry = tokens.find(entry => {
              const entryToken = typeof entry === 'string' ? entry : (entry.token as string)
              return entryToken.toLowerCase() === tokenLower
            })
            if (existingEntry && typeof existingEntry === 'object' && existingEntry.origin) {
              newEntry.origin = existingEntry.origin
              break
            }
          }
          
          tokenPatterns[newField].push(newEntry)
        }
      }
      
      config.token_patterns = tokenPatterns

      // ── 3. Update auto_answers (use original case) ──
      for (const r of resolutions) {
        if (!r.confirmed) continue
        if (r.split) continue  // Skip split tokens - they're handled separately
        
        const token = r.token
        
        // Validate field name (Bug 11)
        const validFields = ['subject', 'room', 'class_name', 'teacher_code', 'time_slot', 'lesson_number', 'ignore']
        if (!validFields.includes(r.field)) {
          console.warn(`[TemplateEditModal] Invalid field name: ${r.field}, skipping`)
          continue
        }
        
        // Case-insensitive lookup for existing entry
        const existingKey = Object.keys(autoAnswers).find(k => k.toLowerCase() === token.toLowerCase())
        
        if (existingKey && autoAnswers[existingKey]) {
          // Update existing entry
          autoAnswers[existingKey].field = r.field
          autoAnswers[existingKey].confirmed = true
          autoAnswers[existingKey].confidence = 0.95
          autoAnswers[existingKey].source = 'template_pattern'
        } else {
          // Create new entry with original case
          autoAnswers[token] = {
            field: r.field,
            confirmed: true,
            confidence: 0.95,
            source: 'template_pattern',
          }
        }
      }
      
      config.auto_answers = autoAnswers

      // ── 4. Save to backend ──
      await templateTrainingApi.updateTemplate(templateId, { extraction_config: config })
      
      // ── 5. Invalidate cache ──
      timetableImportV2Api.invalidateTokenStatusCache(templateId)
      
      onSaved()
      onClose()
    } catch (e: unknown) {
      console.error('[TemplateEditModal] Save failed:', e)
      setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen')
    }
  }, [templateId, onSaved, onClose])

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 flex items-center gap-3">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          <span className="text-sm text-gray-500">Template-Daten laden...</span>
        </div>
      </div>
    )
  }

  if (error && matchedTokens.length === 0 && unmatchedTokens.length === 0) {
    return null
  }

  return (
    <>
      {error && (
        <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          {error}
        </div>
      )}
      <TokenMatchingModal
        isOpen={isOpen && !loading}
        templateId={templateId}
        matchedTokens={matchedTokens}
        unmatchedTokens={unmatchedTokens}
        tokenPositions={tokenPositions}
        orphanPatterns={orphanPatterns}
        splitTracking={splitTracking}
        onResolve={handleResolve}
        onCancel={onClose}
      />
    </>
  )
}
